<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

defineProps({
  rows: { type: Array, default: () => [] },
  groupBy: { type: String, default: 'agent' },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['groupChange']);
const { t } = useI18n();

const groupOptions = computed(() => [
  { key: 'agent', label: t('CSAT_REPORTS.BREAKDOWN.GROUPS.AGENT') },
  { key: 'team', label: t('CSAT_REPORTS.BREAKDOWN.GROUPS.TEAM') },
  { key: 'source', label: t('CSAT_REPORTS.BREAKDOWN.GROUPS.SOURCE') },
  { key: 'tag', label: t('CSAT_REPORTS.BREAKDOWN.GROUPS.TAG') },
  {
    key: 'bot_operator',
    label: t('CSAT_REPORTS.BREAKDOWN.GROUPS.BOT_OPERATOR'),
  },
]);

const formatAverage = value =>
  value === null || value === undefined ? '—' : Number(value).toFixed(1);

const formatPercent = value =>
  value === null || value === undefined ? '—' : `${value}%`;
</script>

<template>
  <section
    class="overflow-hidden shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <div
      class="flex flex-wrap items-center justify-between gap-3 border-b border-n-weak px-6 py-4"
    >
      <h3 class="mb-0 text-base font-medium text-n-slate-12">
        {{ $t('CSAT_REPORTS.BREAKDOWN.TITLE') }}
      </h3>
      <div class="flex flex-wrap gap-1">
        <button
          v-for="option in groupOptions"
          :key="option.key"
          type="button"
          class="rounded-lg px-2.5 py-1.5 text-xs transition"
          :class="
            groupBy === option.key
              ? 'bg-n-brand text-white'
              : 'border border-n-weak text-n-slate-11 hover:text-n-slate-12'
          "
          @click="emit('groupChange', option.key)"
        >
          {{ option.label }}
        </button>
      </div>
    </div>

    <div v-if="isLoading" class="h-48 animate-pulse bg-n-alpha-2" />
    <div v-else-if="rows.length" class="overflow-x-auto">
      <table class="w-full">
        <thead class="border-b border-n-weak">
          <tr>
            <th class="px-6 py-3 text-left text-sm font-medium text-n-slate-12">
              {{ $t('CSAT_REPORTS.BREAKDOWN.NAME') }}
            </th>
            <th class="px-6 py-3 text-left text-sm font-medium text-n-slate-12">
              {{ $t('CSAT_REPORTS.BREAKDOWN.AVERAGE') }}
            </th>
            <th class="px-6 py-3 text-left text-sm font-medium text-n-slate-12">
              {{ $t('CSAT_REPORTS.BREAKDOWN.RESPONSES') }}
            </th>
            <th class="px-6 py-3 text-left text-sm font-medium text-n-slate-12">
              {{ $t('CSAT_REPORTS.BREAKDOWN.SATISFACTION') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr v-for="row in rows" :key="`${row.group_type}-${row.id}`">
            <td class="px-6 py-3 text-sm text-n-slate-12">{{ row.name }}</td>
            <td class="px-6 py-3 text-sm tabular-nums text-n-slate-12">
              {{ formatAverage(row.average) }}
            </td>
            <td class="px-6 py-3 text-sm tabular-nums text-n-slate-12">
              {{ row.responses }}
            </td>
            <td class="px-6 py-3 text-sm tabular-nums text-n-slate-12">
              {{ formatPercent(row.satisfaction_rate) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-else class="px-6 py-12 text-center text-sm text-n-slate-11">
      {{ $t('CSAT_REPORTS.BREAKDOWN.EMPTY') }}
    </p>
  </section>
</template>
